import { useEffect, useState } from 'react'
import type {
  Participant,
  RemoteTrackPublication,
  LocalTrackPublication,
  Room,
} from 'livekit-client'

// Polled bandwidth per participant. Returns the most recent
// total-of-all-tracks throughput in kbps. For the local participant we
// look at `outbound-rtp` stats (uplink to SFU); for remote participants
// we look at `inbound-rtp` stats (downlink from SFU). Updates every
// 1.5s so the number doesn't flicker on every short burst.
export function useParticipantBandwidth(
  participant: Participant | null | undefined,
  isLocal: boolean,
): number | null {
  const [kbps, setKbps] = useState<number | null>(null)

  useEffect(() => {
    if (!participant) {
      setKbps(null)
      return
    }
    let lastBytes = 0
    let lastTs = 0
    let cancelled = false
    let timer: ReturnType<typeof setInterval> | null = null

    const sample = async () => {
      if (cancelled) return
      try {
        let totalBytes = 0
        // Iterate every published/subscribed track on the participant
        // and pull its RTC stats. We sum across audio + video + screen
        // because the user wants to see total stream throughput, not a
        // per-track breakdown.
        const pubs = Array.from(participant.trackPublications.values()) as Array<
          LocalTrackPublication | RemoteTrackPublication
        >
        for (const pub of pubs) {
          const track = pub.track
          if (!track) continue
          const report = await track.getRTCStatsReport().catch(() => undefined)
          if (!report) continue
          report.forEach((stat: any) => {
            if (isLocal && stat.type === 'outbound-rtp' && typeof stat.bytesSent === 'number') {
              totalBytes += stat.bytesSent
            } else if (!isLocal && stat.type === 'inbound-rtp' && typeof stat.bytesReceived === 'number') {
              totalBytes += stat.bytesReceived
            }
          })
        }
        const now = performance.now()
        if (lastTs > 0 && totalBytes >= lastBytes) {
          const dtSec = (now - lastTs) / 1000
          if (dtSec > 0) {
            const bps = ((totalBytes - lastBytes) * 8) / dtSec
            // Smooth a touch — single-sample bursts overstate the real
            // average. 70/30 EMA is enough to settle.
            setKbps((prev) => {
              const next = bps / 1000
              if (prev == null) return next
              return prev * 0.3 + next * 0.7
            })
          }
        }
        lastBytes = totalBytes
        lastTs = now
      } catch {
        // ignore — stats can briefly be unavailable during reconnect
      }
    }

    sample()
    timer = setInterval(sample, 1500)
    return () => {
      cancelled = true
      if (timer) clearInterval(timer)
    }
  }, [participant, isLocal])

  return kbps
}

// Compact human-readable formatter: "1.4 Mbps", "320 kbps", "—" when
// stats aren't available yet.
export function formatBandwidth(kbps: number | null): string {
  if (kbps == null || !isFinite(kbps) || kbps < 1) return '—'
  if (kbps >= 1000) return `${(kbps / 1000).toFixed(1)} Mbps`
  return `${Math.round(kbps)} kbps`
}

// Self-tile bandwidth: BOTH directions in one hook because the user
// needs to see what their own browser is uploading to the SFU AND what
// it's pulling down (sum across every remote's subscribed tracks).
// `useParticipantBandwidth(localParticipant, true)` already covers OUT
// — but for IN there's no LiveKit "self inbound" Participant, so we
// walk remoteParticipants here and aggregate inbound-rtp bytesReceived
// across the whole room. Same 1.5s polling cadence + EMA smoothing as
// the per-participant hook so the two chips read consistent.
export function useSelfBandwidth(
  room: Room | null | undefined,
): { inKbps: number | null; outKbps: number | null } {
  const [state, setState] = useState<{ inKbps: number | null; outKbps: number | null }>({
    inKbps: null,
    outKbps: null,
  })

  useEffect(() => {
    if (!room) {
      setState({ inKbps: null, outKbps: null })
      return
    }
    let lastOutBytes = 0
    let lastInBytes = 0
    let lastTs = 0
    let cancelled = false
    let timer: ReturnType<typeof setInterval> | null = null

    const sample = async () => {
      if (cancelled) return
      try {
        let outBytes = 0
        let inBytes = 0

        // Uplink: every locally-published track contributes
        // outbound-rtp bytesSent. Same path as the per-participant
        // hook's isLocal branch.
        const localPubs = Array.from(
          room.localParticipant.trackPublications.values()
        ) as LocalTrackPublication[]
        for (const pub of localPubs) {
          const track = pub.track
          if (!track) continue
          const report = await track.getRTCStatsReport().catch(() => undefined)
          if (!report) continue
          report.forEach((stat: any) => {
            if (stat.type === 'outbound-rtp' && typeof stat.bytesSent === 'number') {
              outBytes += stat.bytesSent
            }
          })
        }

        // Downlink: walk every remote participant's subscribed tracks
        // and sum inbound-rtp bytesReceived. This is the total stream
        // the user is pulling from the SFU — audio + video + screen
        // shares of everyone else.
        for (const remote of room.remoteParticipants.values()) {
          const pubs = Array.from(remote.trackPublications.values()) as RemoteTrackPublication[]
          for (const pub of pubs) {
            const track = pub.track
            if (!track) continue
            const report = await track.getRTCStatsReport().catch(() => undefined)
            if (!report) continue
            report.forEach((stat: any) => {
              if (stat.type === 'inbound-rtp' && typeof stat.bytesReceived === 'number') {
                inBytes += stat.bytesReceived
              }
            })
          }
        }

        const now = performance.now()
        if (lastTs > 0) {
          const dtSec = (now - lastTs) / 1000
          if (dtSec > 0) {
            // Guard against monotonic counter resets (track unpublish
            // replaces the underlying RTCRtpSender). If the new total
            // is lower than what we saw last sample, treat as a fresh
            // window instead of computing a negative bps.
            const outBps = outBytes >= lastOutBytes ? ((outBytes - lastOutBytes) * 8) / dtSec : 0
            const inBps = inBytes >= lastInBytes ? ((inBytes - lastInBytes) * 8) / dtSec : 0
            setState((prev) => ({
              outKbps: prev.outKbps == null ? outBps / 1000 : prev.outKbps * 0.3 + (outBps / 1000) * 0.7,
              inKbps: prev.inKbps == null ? inBps / 1000 : prev.inKbps * 0.3 + (inBps / 1000) * 0.7,
            }))
          }
        }
        lastOutBytes = outBytes
        lastInBytes = inBytes
        lastTs = now
      } catch {
        // Stats can briefly be unavailable during reconnect — skip the
        // tick and try again next interval.
      }
    }

    sample()
    timer = setInterval(sample, 1500)
    return () => {
      cancelled = true
      if (timer) clearInterval(timer)
    }
  }, [room])

  return state
}
